import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import type { UserAnswerDraft } from '@/features/events/conversation/state/types'
import { DraftTimestamps } from './DraftTimestamps'

interface UserAnswerDraftItemProps {
  readonly questionHeader: string
  readonly questionIndex: number
  readonly answerDraft: UserAnswerDraft
  readonly onAnswerDraftClick: (questionHeader: string) => void
}

export const UserAnswerDraftItem: FC<UserAnswerDraftItemProps> = ({
  questionHeader,
  questionIndex,
  answerDraft,
  onAnswerDraftClick,
}) => {
  const theme = useTheme()

  const handleClick = useCallback(() => {
    onAnswerDraftClick(questionHeader)
  }, [questionHeader, onAnswerDraftClick])

  const hasCustomAnswer =
    answerDraft.customAnswer !== null && answerDraft.customAnswer.trim() !== ''

  if (!hasCustomAnswer) {
    return null
  }

  return (
    <Box sx={containerSx} onClick={handleClick}>
      <Box sx={contentSx}>
        <Typography component="span" sx={questionIndexSx(theme)}>
          Q{questionIndex + 1}:
        </Typography>
        <Typography component="span" sx={questionHeaderSx(theme)}>
          {questionHeader}
        </Typography>
      </Box>
      <DraftTimestamps createdAt={answerDraft.createdAt} updatedAt={answerDraft.updatedAt} />
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 0.75,
  px: 2,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  borderRadius: 0.5,
}

const contentSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const questionIndexSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: '0.8125rem',
  color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
  fontWeight: 600,
})

const questionHeaderSx = (theme: Theme): SxProps<Theme> => ({
  fontSize: '0.8125rem',
  color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
  fontWeight: 400,
})
